import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { loadInvoiceList, loadInvoicePdf, sendInvoiceReminderToCustomer } from "../../api/invoicing";

const stateOptions = [
  { value: "all", label: "Alles" },
  { value: "draft", label: "Concept" },
  { value: "open", label: "Open" },
  { value: "late", label: "Te laat" },
  { value: "reminded", label: "Herinnerd" },
  { value: "paid", label: "Betaald" },
];

const periodOptions = [
  { value: "this_year", label: "Dit jaar" },
  { value: "last_year", label: "Vorig jaar" },
  { value: "this_quarter", label: "Dit kwartaal" },
  { value: "this_month", label: "Deze maand" },
];

const stateLabelMap = {
  draft: "Concept",
  open: "Open",
  late: "Te laat",
  reminded: "Herinnerd",
  paid: "Betaald",
};

const chipColor = (state) => {
  if (state === "paid") {
    return "success";
  }

  if (state === "late") {
    return "error";
  }

  if (state === "reminded") {
    return "warning";
  }

  return "default";
};

const canSendReminder = (state) => ["open", "late", "reminded"].includes(state);

const InvoiceOverview = ({ pin }) => {
  const [filters, setFilters] = useState({ state: "all", period: "this_year" });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyInvoiceId, setBusyInvoiceId] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewState, setPreviewState] = useState({
    open: false,
    loading: false,
    invoiceNumber: "",
    pdfUrl: "",
  });

  useEffect(() => () => {
    if (previewState.pdfUrl) {
      URL.revokeObjectURL(previewState.pdfUrl);
    }
  }, [previewState.pdfUrl]);

  const refreshInvoices = useCallback(async (showSpinner = false) => {
    setError("");
    if (showSpinner) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const result = await loadInvoiceList({
        pin,
        state: filters.state,
        period: filters.period,
      });
      setInvoices(result.invoices || []);
    } catch (loadError) {
      setError(loadError.message || "Facturen laden is niet gelukt.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters.period, filters.state, pin]);

  useEffect(() => {
    refreshInvoices(true);
  }, [refreshInvoices]);

  const totals = useMemo(() => ({
    total: invoices.length,
    unpaid: invoices.filter((invoice) => Number(invoice.totalUnpaid || 0) > 0).length,
    late: invoices.filter((invoice) => invoice.state === "late").length,
  }), [invoices]);

  const handleReminder = async (invoice) => {
    setBusyInvoiceId(invoice.id);
    setError("");
    setSuccess("");

    try {
      await sendInvoiceReminderToCustomer({
        pin,
        invoiceId: invoice.id,
        contactId: invoice.contactId,
        workflowId: invoice.workflowId,
        documentStyleId: invoice.documentStyleId,
        identityId: invoice.identityId,
        email: invoice.email,
      });
      setSuccess(`Herinnering voor factuur ${invoice.invoiceId || invoice.id} is verstuurd.`);
      await refreshInvoices();
    } catch (reminderError) {
      setError(reminderError.message || "Herinnering sturen is niet gelukt.");
    } finally {
      setBusyInvoiceId("");
    }
  };

  const closePreview = () => {
    setPreviewState((current) => {
      if (current.pdfUrl) {
        URL.revokeObjectURL(current.pdfUrl);
      }

      return {
        open: false,
        loading: false,
        invoiceNumber: "",
        pdfUrl: "",
      };
    });
  };

  const handlePreview = async (invoice) => {
    setError("");
    setSuccess("");
    setPreviewState((current) => {
      if (current.pdfUrl) {
        URL.revokeObjectURL(current.pdfUrl);
      }

      return {
        open: true,
        loading: true,
        invoiceNumber: invoice.invoiceId || invoice.id,
        pdfUrl: "",
      };
    });

    try {
      const pdfBlob = await loadInvoicePdf({ pin, invoiceId: invoice.id });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPreviewState({
        open: true,
        loading: false,
        invoiceNumber: invoice.invoiceId || invoice.id,
        pdfUrl,
      });
    } catch (previewError) {
      setPreviewState({
        open: false,
        loading: false,
        invoiceNumber: "",
        pdfUrl: "",
      });
      setError(previewError.message || "Factuur ophalen is niet gelukt.");
    }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                Factuuroverzicht
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Standaard tonen we dit jaar. Gebruik de filters om snel openstaande of betaalde facturen te bekijken.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={() => refreshInvoices()}
              disabled={refreshing}
            >
              Vernieuwen
            </Button>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto auto auto" }, gap: 2, alignItems: "center" }}>
            <TextField
              select
              label="Status"
              value={filters.state}
              onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}
              fullWidth
            >
              {stateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Periode"
              value={filters.period}
              onChange={(event) => setFilters((current) => ({ ...current, period: event.target.value }))}
              fullWidth
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Chip label={`${totals.total} totaal`} />
            <Chip label={`${totals.unpaid} open`} color={totals.unpaid > 0 ? "warning" : "default"} />
            <Chip label={`${totals.late} te laat`} color={totals.late > 0 ? "error" : "default"} />
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : invoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Geen facturen gevonden voor deze filter.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {invoices.map((invoice) => (
                <Card key={invoice.id} sx={{ borderRadius: 2, border: "1px solid rgba(36,53,45,0.08)", boxShadow: "none" }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr auto" }, gap: 2, alignItems: "center" }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                          {invoice.contactName || "Onbekende klant"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Factuur {invoice.invoiceId || invoice.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Datum: {invoice.date || "-"} | Vervaldatum: {invoice.dueDate || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Totaal incl. btw: {invoice.totalPriceInclTax || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Betaald: {invoice.totalPaid || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Openstaand: {invoice.totalUnpaid || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Herinneringen: {invoice.reminderCount || 0}
                        </Typography>
                      </Box>

                      <Stack spacing={1.5} sx={{ minWidth: 170 }}>
                        <Chip
                          label={stateLabelMap[invoice.state] || invoice.state || "Onbekend"}
                          color={chipColor(invoice.state)}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<VisibilityOutlinedIcon />}
                          onClick={() => handlePreview(invoice)}
                        >
                          Bekijk factuur
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={busyInvoiceId === invoice.id ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                          disabled={!canSendReminder(invoice.state) || busyInvoiceId === invoice.id}
                          onClick={() => handleReminder(invoice)}
                        >
                          Herinnering sturen
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
      <Dialog
        open={previewState.open}
        onClose={closePreview}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Factuur {previewState.invoiceNumber}</DialogTitle>
        <DialogContent dividers>
          {previewState.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 420, md: 760 },
                  border: "1px solid rgba(36,53,45,0.12)",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                {previewState.pdfUrl ? (
                  <Box
                    component="object"
                    data={previewState.pdfUrl}
                    type="application/pdf"
                    aria-label={`Factuur ${previewState.invoiceNumber}`}
                    sx={{
                      display: "block",
                      width: "100%",
                      minHeight: { xs: 420, md: 760 },
                      border: 0,
                    }}
                  >
                    <Stack spacing={2} sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Deze telefoon opent PDF's niet direct in de pagina. Gebruik de knop hieronder om de factuur toch te bekijken.
                      </Typography>
                      <Button
                        variant="outlined"
                        href={previewState.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Factuur openen
                      </Button>
                    </Stack>
                  </Box>
                ) : null}
              </Box>
              {previewState.pdfUrl ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="outlined"
                    href={previewState.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open apart
                  </Button>
                </Box>
              ) : null}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InvoiceOverview;
